/**
 * Flickity bootstrap — optimized for many carousels per page (7–8+).
 * - Shared resize + batched RAF
 * - Delegated image load
 * - Lazy init offscreen (storefront)
 * - Editor-safe lifecycle cleanup
 */
const instances = new WeakMap();
const autoHeightRoots = new Set();

let batchRafId = 0;
let resizeTimer = 0;
let sharedListenersBound = false;
let initObserver = null;

const isDesignMode = () =>
  window.Shopify?.designMode === true ||
  document.documentElement.classList.contains('shopify-design-mode');

function parseSettings(element) {
  try {
    return JSON.parse(element.getAttribute('data-flickity') || '{}');
  } catch {
    return {};
  }
}

function isAutoHeightMode(element) {
  return element.dataset.slideHeight === 'auto';
}

function measureMaxCellHeight(element) {
  const cells = element.querySelectorAll('.carousel-cell');
  if (!cells.length) return 0;

  let max = 0;
  for (let i = 0; i < cells.length; i++) {
    const h = cells[i].offsetHeight;
    if (h > max) max = h;
  }
  return max;
}

function scheduleBatchHeightSync() {
  if (batchRafId) return;
  batchRafId = requestAnimationFrame(() => {
    batchRafId = 0;

    for (const element of autoHeightRoots) {
      const inst = instances.get(element);
      if (inst && !inst.state.destroyed) inst.syncHeight();
    }
  });
}

function onWindowResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(scheduleBatchHeightSync, 160);
}

function onDelegatedImageLoad(event) {
  const img = event.target;
  if (!(img instanceof HTMLImageElement)) return;

  const root = img.closest('.flicktiy-carousel');
  if (!root || !isAutoHeightMode(root)) return;

  scheduleBatchHeightSync();
}

function bindSharedListenersOnce() {
  if (sharedListenersBound) return;
  sharedListenersBound = true;

  window.addEventListener('resize', onWindowResize, { passive: true });
  document.addEventListener('load', onDelegatedImageLoad, true);
  window.addEventListener('load', scheduleBatchHeightSync, { once: true });
}

function getInitObserver() {
  if (initObserver) return initObserver;

  initObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        initObserver.unobserve(entry.target);
        initCarousel(entry.target);
      }
    },
    { root: null, rootMargin: '240px 0px', threshold: 0 }
  );

  return initObserver;
}

function createCarouselApi(element, flkty) {
  const state = {
    destroyed: false,
    lastHeight: null,
    resizeLocked: false,
    visibilityObserver: null,
  };

  const syncHeight = () => {
    if (state.destroyed) return;

    if (!isAutoHeightMode(element)) {
      if (state.lastHeight === null) return;

      element.style.removeProperty('--slide-min-height');
      element.style.removeProperty('--slide-min-height-desktop');
      state.lastHeight = null;

      if (!state.resizeLocked) {
        state.resizeLocked = true;
        flkty.resize();
        requestAnimationFrame(() => {
          state.resizeLocked = false;
        });
      }
      return;
    }

    const max = measureMaxCellHeight(element);
    if (!max || max === state.lastHeight) return;

    state.lastHeight = max;
    element.style.setProperty('--slide-min-height', `${max}px`);
    element.style.setProperty('--slide-min-height-desktop', `${max}px`);

    if (!state.resizeLocked) {
      state.resizeLocked = true;
      flkty.resize();
      requestAnimationFrame(() => {
        state.resizeLocked = false;
      });
    }
  };

  const bindVisibilityPause = () => {
    if (!('IntersectionObserver' in window)) return;
    if (!flkty.options.autoPlay) return;

    state.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        if (state.destroyed) return;
        if (entry.isIntersecting) flkty.playPlayer?.();
        else flkty.stopPlayer?.();
      },
      { threshold: 0.05 }
    );

    state.visibilityObserver.observe(element);
  };

  const destroy = () => {
    if (state.destroyed) return;
    state.destroyed = true;

    autoHeightRoots.delete(element);
    state.visibilityObserver?.disconnect();

    try {
      flkty.destroy();
    } catch (_) {
      /* noop */
    }

    instances.delete(element);
  };

  return { state, syncHeight, bindVisibilityPause, destroy };
}

function initCarousel(element) {
  if (!(element instanceof HTMLElement)) return;
  if (instances.has(element) || Flickity.data(element)) return;

  bindSharedListenersOnce();

  const flkty = new Flickity(element, parseSettings(element));
  const api = createCarouselApi(element, flkty);

  if (isAutoHeightMode(element)) {
    autoHeightRoots.add(element);
    flkty.on('ready', api.syncHeight);
    flkty.on('lazyLoad', api.syncHeight);
    api.syncHeight();
  }

  api.bindVisibilityPause();

  instances.set(element, api);
}

function queueCarouselInit(element) {
  if (!(element instanceof HTMLElement)) return;
  if (instances.has(element) || Flickity.data(element)) return;

  // Theme editor: init ngay để preview ổn định
  if (isDesignMode()) {
    initCarousel(element);
    return;
  }

  // Storefront: carousel gần/ trong viewport init ngay
  const rect = element.getBoundingClientRect();
  const nearViewport = rect.top < window.innerHeight + 240 && rect.bottom > -240;

  if (nearViewport || !('IntersectionObserver' in window)) {
    initCarousel(element);
    return;
  }

  getInitObserver().observe(element);
}

function initAll(root = document) {
  root.querySelectorAll('.flicktiy-carousel').forEach(queueCarouselInit);
}

function destroyBySection(sectionEl) {
  if (!sectionEl) return;

  sectionEl.querySelectorAll('.flicktiy-carousel').forEach((element) => {
    const inst = instances.get(element);
    inst?.destroy();
    initObserver?.unobserve(element);
  });
}

function boot() {
  bindSharedListenersOnce();
  initAll(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyBySection(event.target);
});

// Chỉ init carousel mới trong section vừa load — không quét lại cả document
document.addEventListener('shopify:block:select', (event) => {
  const root = event.target?.closest?.('.shopify-section');
  if (root) initAll(root);
});